import avenirNextDemiWoff2 from "@core-ui/fonts/assets/8c6d7851-9978-4520-8c8f-dc1b38b36eb6.woff2";
import avenirNextMediumWoff2 from "@core-ui/fonts/assets/a1049d00-54ad-4589-95b8-d353f7ab52f0.woff2";
import avenirNextRegularWoff2 from "@core-ui/fonts/assets/f64c3634-1027-4aec-8bd4-d96f2444f559.woff2";
import avenirNextBoldWoff2 from "@core-ui/fonts/assets/9544095b-fbf6-4d50-870f-a5102776eae4.woff2";

import bananaSerifRegularWoff2 from "@core-ui/fonts/assets/BananaText-Regular.woff2";
import bananaSerifLightWoff2 from "@core-ui/fonts/assets/Banana-Regular.woff2";
import bananaSerifLightItalicWoff2 from "@core-ui/fonts/assets/Banana-Italic.woff2";

import gothamBlackWoff2 from "@core-ui/fonts/assets/Gotham-Black_Web.woff2";
import gothamBoldWoff2 from "@core-ui/fonts/assets/Gotham-Bold_Web.woff2";
import gothamBookWoff2 from "@core-ui/fonts/assets/Gotham-Book_Web.woff2";
import gothamMediumWoff2 from "@core-ui/fonts/assets/Gotham-Medium_Web.woff2";

import lynstoneBookWoff2 from "@core-ui/fonts/assets/LynstoneWeb-Book.woff2";
import lynstoneSemiBoldWoff2 from "@core-ui/fonts/assets/LynstoneWeb-SemiBold.woff2";

import playfairWoff2 from "@core-ui/fonts/assets/Playfair-VariableFont_wght.woff2";

import sourceSansProLightWoff from "@core-ui/fonts/assets/SourceSansPro-Light.woff";
import sourceSansProRegularWoff from "@core-ui/fonts/assets/sourcesanspro-regular-webfont.woff";
import sourceSansProSemiBold from "@core-ui/fonts/assets/source-sans-pro-v14-latin-600.woff2";
import sourceSansProBoldWoff from "@core-ui/fonts/assets/sourcesanspro-bold-webfont.woff";
import {CSSObject} from "@emotion/react";
import {isStorybook, Brands} from "./internal";
import {getFontWeight} from "./getFont";

type Src = {
  woff2?: string;
  woff?: string;
};

type FontFamily = Pick<CSSObject, "fontFamily" | "fontWeight" | "fontStyle"> & {
  src: Src;
  fontDisplay?: string;
};

export const constructSrc = ({woff2, woff}: Src): string => `
  ${woff2 ? `url(${woff2}) format("woff2"),` : ""}
  url(${woff}) format("woff");
  `;

const atFontFamilies: FontFamily[] = [
  {
    fontFamily: "Avenir Next",
    fontStyle: "normal",
    ...getFontWeight("regular"),
    src: {
      woff2: avenirNextRegularWoff2,
    },
  },
  {
    fontFamily: "Avenir Next",
    fontStyle: "normal",
    ...getFontWeight("medium"),
    src: {
      woff2: avenirNextMediumWoff2,
    },
  },
  {
    fontFamily: "Avenir Next",
    fontStyle: "normal",
    ...getFontWeight("demiBold"),
    src: {
      woff2: avenirNextDemiWoff2,
    },
  },
  {
    fontFamily: "Avenir Next",
    fontStyle: "normal",
    ...getFontWeight("bold"),
    src: {
      woff2: avenirNextBoldWoff2,
    },
  },
  {
    fontFamily: "Playfair",
    fontStyle: "normal",
    ...getFontWeight("regular"),
    src: {
      woff2: playfairWoff2,
    },
  },
];

const brFontFamilies: FontFamily[] = [
  {
    fontFamily: "Lynstone",
    fontStyle: "normal",
    ...getFontWeight("semiBold"),
    src: {
      woff2: lynstoneSemiBoldWoff2,
    },
  },
  {
    fontFamily: "Lynstone",
    fontStyle: "normal",
    ...getFontWeight("book"),
    src: {
      woff2: lynstoneBookWoff2,
    },
  },
  {
    fontFamily: "BananaSerif",
    fontStyle: "normal",
    ...getFontWeight("regular"),
    src: {
      woff2: bananaSerifRegularWoff2,
    },
  },
  {
    fontFamily: "BananaSerif",
    fontStyle: "normal",
    ...getFontWeight("light"),
    src: {
      woff2: bananaSerifLightWoff2,
    },
  },
  {
    fontFamily: "BananaSerif",
    fontStyle: "italic",
    ...getFontWeight("light"),
    src: {
      woff2: bananaSerifLightItalicWoff2,
    },
  },
];

const onFontFamilies = [
  {
    fontFamily: "Gotham",
    fontStyle: "normal",
    ...getFontWeight("book"),
    src: {
      woff2: gothamBookWoff2,
    },
  },
  {
    fontFamily: "Gotham",
    fontStyle: "normal",
    ...getFontWeight("medium"),
    src: {
      woff2: gothamMediumWoff2,
    },
  },
  {
    fontFamily: "Gotham",
    fontStyle: "normal",
    ...getFontWeight("bold"),
    src: {
      woff2: gothamBoldWoff2,
    },
  },
  {
    fontFamily: "Gotham",
    fontStyle: "normal",
    ...getFontWeight("black"),
    src: {
      woff2: gothamBlackWoff2,
    },
  },
];

type FontTypes = Brands | "crossBrand";

export function flatMap<T, TRet>(coll: T[], fn: (el: T) => TRet[]): TRet[] {
  return coll.map(fn).reduce((acc, el) => acc.concat(el), []);
}

function createFontFamilies(
  enabledFeatures?: Record<string, boolean>
): Record<FontTypes, FontFamily[]> {
  const fonts = {
    [Brands.Athleta]: atFontFamilies,
    [Brands.BananaRepublic]: brFontFamilies,
    [Brands.BananaRepublicFactoryStore]: brFontFamilies,
    [Brands.Gap]: [],
    [Brands.GapFactoryStore]: [],
    [Brands.OldNavy]: onFontFamilies,
    crossBrand: [
      {
        fontFamily: "Source Sans Pro",
        fontStyle: "normal",
        ...getFontWeight("light"),
        src: {
          woff: sourceSansProLightWoff,
        },
      },
      {
        fontFamily: "Source Sans Pro",
        fontStyle: "normal",
        ...getFontWeight("regular"),
        src: {
          woff: sourceSansProRegularWoff,
        },
      },
      {
        fontFamily: "Source Sans Pro",
        fontStyle: "normal",
        ...getFontWeight("semiBold"),
        src: {
          woff2: sourceSansProSemiBold,
        },
      },
      {
        fontFamily: "Source Sans Pro",
        fontStyle: "normal",
        ...getFontWeight("bold"),
        src: {
          woff: sourceSansProBoldWoff,
        },
      },
    ],
  };

  return fonts;
}

export const fontFamilies = createFontFamilies();

function createFontFaces(
  brand: Brands,
  enabledFeatures?: Record<string, boolean>
): CSSObject[] {
  const rsFontFamilies = createFontFamilies(enabledFeatures);

  const useFontDisplaySwap =
    enabledFeatures && enabledFeatures["enable-font-display-swap"];
  const fontDisplayFeatureFlag = useFontDisplaySwap ? "swap" : "auto";

  const makeFontFamily = ({
    src,
    fontDisplay,
    ...font
  }: FontFamily): CSSObject => ({
    "@font-face": {
      ...font,
      fontDisplay: fontDisplayFeatureFlag,
      src: constructSrc(src),
    },
  });
  const fontTypes: FontTypes[] = [brand, "crossBrand"];
  return flatMap(fontTypes, (fontType) =>
    rsFontFamilies[fontType].map(makeFontFamily)
  );
}
type WindowWithFonts = typeof window & {
  fontsLoaded: Brands[];
};
export function globalFonts(
  brand: Brands,
  enabledFeatures?: Record<string, boolean>
): CSSObject[] {
  if (typeof window !== "undefined" && !isStorybook) {
    const fontsAreMissing =
      (window as WindowWithFonts)?.fontsLoaded === undefined;
    const fonts = fontsAreMissing
      ? [brand]
      : (window as WindowWithFonts).fontsLoaded;
    if (!fontsAreMissing && fonts.includes(brand)) return [];
    if (fontsAreMissing) {
      (window as WindowWithFonts).fontsLoaded = fonts;
    } else {
      fonts.push(brand);
    }
    return createFontFaces(brand, enabledFeatures);
  }
  return createFontFaces(brand, enabledFeatures);
}
